'use client';

import { IBlogArticleListItem, IBlogAuthorInfo, IBlogConfig } from '@/api/types/blog';
import InfiniteScroll from '@/components/InfiniteScroll';
import Pagination from '@/components/Pagination';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { scrollToAnchor } from '@/utils/tools';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import ArticleCard from './ArticleCard';
import AuthorNavList from './AuthorNavList';
import BlogNoArticle from './BlogNoArticle';
import { useTranslations } from 'next-intl';

interface AuthorArticleListProps {
  articles: IBlogArticleListItem[];
  page: number;
  total: number;
  loading?: boolean;
  authorInfo: IBlogAuthorInfo;
  navList: IBlogConfig[];
  isWeb?: boolean;
  className?: string;
  onPageChange?: (page: number) => void;
  onLoadMore?: () => void;
  onArticleExp?: (article: IBlogArticleListItem, index: number) => void;
  onArticleClick?: (article: IBlogArticleListItem, index: number) => void;
  onAuthorClick?: (article: IBlogArticleListItem, index: number) => void;
}

const AuthorArticleList: React.FC<AuthorArticleListProps> = ({
  articles,
  page,
  total,
  loading = false,
  authorInfo,
  navList,
  isWeb = false,
  className = '',
  onPageChange,
  onLoadMore,
  onArticleExp,
  onArticleClick,
  onAuthorClick,
}) => {
  const t = useTranslations('blog.components');
  const [limit] = useState<number>(18);
  const { deviceType } = useContext(ResponsiveContext);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  // 处理滚动到底部
  const handleReachBottom = useCallback(() => {
    setIsShowFooterNav(false);
    onLoadMore?.();
  }, [onLoadMore]);

  // 处理分页
  const handlePaginationChange = (_page: number) => {
    scrollToAnchor('.article-list-anchor', 50, 'instant');
    setTimeout(() => {
      onPageChange?.(_page);
    }, 500);
  };

  useEffect(() => {
    if (isWeb) return;
    if (Array.isArray(articles) && articles.length >= total) {
      setIsShowFooterNav(true);
    } else {
      setIsShowFooterNav(false);
    }
  }, [articles, total]);

  return (
    <div className={`${className} ${isWeb ? 'min-h-[60vh]' : 'pt-36 px-14'}`}>
      <div className={`article-list-anchor ${isWeb ? 'pt-[80px]' : ''}`} />
      <h2 className={`font-bb ${isWeb ? 'text-32' : 'text-20'}`}>
        {t('articlesBy')} {authorInfo.name}
      </h2>
      {Array.isArray(articles) && articles.length > 0 && (
        <div
          className={`${isWeb ? 'mt-[32px] pb-24 grid grid-cols-3 gap-x-[24px] gap-y-[64px] [@media(max-width:1279px)]:grid-cols-2 [@media(max-width:980px)]:grid-cols-1' : 'mt-20'}`}
        >
          {articles.map((article, index) => (
            <ArticleCard
              key={article.id}
              article={article}
              showIntro
              onArticleExp={(_article) => onArticleExp?.(_article, index)}
              onArticleClick={(_article) => onArticleClick?.(_article, index)}
              onAuthorClick={(_article) => onAuthorClick?.(_article, index)}
              className={`${isWeb ? '' : 'mb-[80px] last:mb-0'}`}
            />
          ))}
        </div>
      )}
      {Array.isArray(articles) && articles.length === 0 && !loading && <BlogNoArticle />}
      {Array.isArray(articles) && articles.length === 0 && loading && <div className="h-[60px]" />}
      <WapOnly>
        <InfiniteScroll
          onReachBottom={handleReachBottom}
          loading={loading}
          page={page}
          total={total}
          limit={limit}
        />
      </WapOnly>
      <WebOnly>
        {total > limit && (
          <Pagination
            current={page}
            total={total}
            pageSize={limit}
            onChange={(event, value) => handlePaginationChange(value)}
            className="mt-[24px]"
            sx={{
              '& .Mui-disabled': {
                color: '#28282822 !important',
                backgroundColor: 'transparent',
              },
            }}
          />
        )}
      </WebOnly>
      {/* 底部导航 */}
      {(articles.length === total || deviceType === EPlatform.WEB) && (
        <AuthorNavList navList={navList} isWeb={isWeb} />
      )}
    </div>
  );
};

export default AuthorArticleList;
